/**
 * 2021-2024 Stadt Wolfsburg
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from 'react';
import {connect} from 'react-redux';

import isEqual from 'lodash.isequal';
import PropTypes from 'prop-types';
import {changeRotation, zoomToPoint} from 'qwc2/actions/map';
import TaskBar from 'qwc2/components/TaskBar';
import ButtonBar from 'qwc2/components/widgets/ButtonBar';
import LocaleUtils from 'qwc2/utils/LocaleUtils';

import './style/MapViewHistory.css';

class WobMapViewHistory extends React.Component {
    static propTypes = {
        changeRotation: PropTypes.func,
        history: PropTypes.array,
        historyPosition: PropTypes.number,
        limit: PropTypes.number,
        loadedHistoryElement: PropTypes.object,
        map: PropTypes.object,
        zoomToPoint: PropTypes.func
    };
    static defaultProps = {
        limit: 10
    };
    state = {
        historyPosition: -1,
        history: [],
        loadedHistoryElement: {
            center: [0, 0],
            projection: "",
            rotation: 0,
            zoom: 0
        }
    };
    componentDidUpdate(prevProps) {
        if (!isEqual(this.props.map.center, prevProps.map.center) || this.props.map.zoom !== prevProps.map.zoom || this.props.map.bbox.rotation !== prevProps.map.bbox.rotation) {
            if (!isEqual(this.props.map.center, this.state.loadedHistoryElement.center) || this.props.map.zoom !== this.state.loadedHistoryElement.zoom || this.props.map.bbox.rotation !== this.state.loadedHistoryElement.rotation) {
                this.addToHistory();
            }
        }
    }
    addToHistory = () => {
        const historyElement = {
            center: this.props.map.center,
            projection: this.props.map.projection,
            rotation: this.props.map.bbox.rotation,
            zoom: this.props.map.zoom
        };
        if (this.isValidHistoryElement(historyElement)) {
            let newHistory;
            if (this.state.historyPosition > 0 && this.state.historyPosition < this.state.history.length - 1) {
                newHistory = [...this.state.history.slice(0, this.state.historyPosition), historyElement];
            } else {
                newHistory = [...this.state.history, historyElement];
            }
            if (newHistory.length > this.props.limit + 1) {
                newHistory = newHistory.slice(-this.props.limit);
            }
            this.setState({
                historyPosition: newHistory.length - 1,
                history: newHistory,
                loadedHistoryElement: {
                    center: [0, 0],
                    projection: "",
                    rotation: 0,
                    zoom: 0
                }
            });
        }
    };
    isValidHistoryElement = (historyElement) => {
        return !(
            historyElement.center.length === 0 || historyElement.center[0] === 0 || historyElement.center[1] === 0 ||
            historyElement.zoom === 0
        );
    };
    loadFromHistory = (direction) => {
        let newHistoryPosition = -1;
        if (direction === "Backward") {
            newHistoryPosition = this.state.historyPosition - 1;
        } else if (direction === "Forward") {
            newHistoryPosition = this.state.historyPosition + 1;
        }
        if (newHistoryPosition >= 0 && newHistoryPosition <= this.state.history.length - 1) {
            const history =  this.state.history;
            const historyElement = history[newHistoryPosition];
            this.setState({historyPosition: newHistoryPosition, loadedHistoryElement: historyElement});
            this.props.zoomToPoint(historyElement.center, historyElement.zoom, historyElement.projection);
            this.props.changeRotation(historyElement.rotation);
        }
    };
    renderBody = () => {
        const backwardButton = {
            key: "Backward",
            label: LocaleUtils.tr("wob.mapviewhistory.backward"),
            icon: "triangle-left",
            extraClasses: "backward-button",
            disabled: this.state.history.length > 0 && this.state.historyPosition > 0 && this.state.historyPosition <= this.state.history.length - 1 ? false : true
        };
        const forwardButton = {
            key: "Forward",
            label: LocaleUtils.tr("wob.mapviewhistory.forward"),
            icon: "triangle-right",
            extraClasses: "forward-button",
            disabled: this.state.history.length > 0 && this.state.historyPosition >= 0 && this.state.historyPosition < this.state.history.length - 1 ? false : true
        };
        return (
            <span>
                <div className="wob-mapviewhistory-description">{LocaleUtils.tr("wob.mapviewhistory.description")}</div>
                <ButtonBar buttons={[backwardButton, forwardButton]}  onClick={this.loadFromHistory} />
            </span>
        );
    };
    render() {
        return (
            <TaskBar task="WobMapViewHistory">
                {() => ({
                    body: this.renderBody()
                })}
            </TaskBar>
        );
    }
}

const selector = (state) => ({
    map: state.map
});

export default connect(selector, {
    changeRotation: changeRotation,
    zoomToPoint: zoomToPoint
})(WobMapViewHistory);
